import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';

import { TRACKING_EVENT_NAMES } from '@/components/wpl/pricing/src/price-finder/constants.tsx';
import { RadioButtons } from '@/libraries/design/components/radio-buttons/src/radio-buttons.tsx';
import { wacToken } from '@/libraries/design/foundations';

import { useUnitCount, useBillingCycle, useSelector } from '../hooks.ts';
import * as selectors from '../selectors.ts';
import { BillingCycle } from '../types.ts';

export interface PricingCalculatorInputsProps {
	unitCountLabel: string;
	unitsLabel: string;
	dataProductKey: string;
}

export const PricingCalculatorInputs = (props: PricingCalculatorInputsProps) => {
	const { unitCountLabel, unitsLabel, dataProductKey } = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [billingCycle, setBillingCycle] = useBillingCycle();
	const [unitCount, setUnitCount] = useUnitCount();

	const hasMonthlyBilling = useSelector(selectors.hasMonthlyBilling);
	const hasAnnualBilling = useSelector(selectors.hasAnnualBilling);

	const [visualUnitCount, setVisualUnitCount] = useState<number | ''>(
		// @ts-ignore
		unitCount,
	);

	useEffect(() => {
		if (visualUnitCount !== '' && visualUnitCount > 0 && !Number.isNaN(visualUnitCount)) {
			// @ts-ignore
			setUnitCount(visualUnitCount);
		}
	}, [visualUnitCount]);

	const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const stringValue = e.target.value;

		if (stringValue === '') return setVisualUnitCount('');

		// validate max number of digits
		// default is 5
		// TODO: make this configurable
		if (stringValue.length > 6) return;

		const numberValue = parseInt(stringValue, 10);

		if (numberValue < 1) return;
		setVisualUnitCount(numberValue);
	};

	const handleUnitCountBlurChange = () => {
		// @ts-ignore
		if (visualUnitCount === '') setVisualUnitCount(unitCount);
		const data = { unitCount, productKey: dataProductKey };
		createAnalyticsEvent({
			eventType: 'ui',
			eventName: `${TRACKING_EVENT_NAMES.unitTierBox}_blurred`,
			eventComponent: 'input',
			event: 'blurred',
			actionSubject: TRACKING_EVENT_NAMES.unitTierBox,
			action: 'blurred',
			attributes: data,
			data,
		}).fire();
	};

	const handleRadioChange = (billingCycle: string) => {
		const data = {
			billingCycle,
			productKey: dataProductKey,
		};
		createAnalyticsEvent({
			eventType: 'ui',
			eventName: TRACKING_EVENT_NAMES.billingCycleChanged,
			eventComponent: 'radioButton',
			event: 'clicked',
			actionSubject: TRACKING_EVENT_NAMES.billingCycleChanged,
			action: 'clicked',
			attributes: data,
		}).fire();

		setBillingCycle(billingCycle as BillingCycle);
	};

	return (
		<div
			css={css({
				margin: 'auto',
				maxWidth: '1408px',
			})}
		>
			<div
				css={css({
					maxWidth: '400px',
					margin: 'auto',
					[media.above.sm]: {
						maxWidth: '100%',
					},
					[media.below.md]: {
						margin: '0 0 auto',
					},
				})}
			>
				<div
					css={[
						css({
							maxWidth: '400px',
							marginBlockEnd: '24px',
							[media.above.sm]: {
								display: 'flex',
								maxWidth: '100%',
								justifyContent: 'center',
								gap: '80px',
							},
						}),
					]}
				>
					<div
						css={css({
							display: 'flex',
							alignItems: 'center',
							[media.above.sm]: {
								display: 'block',
							},
						})}
					>
						<label
							css={[
								labelStyles,
								css({
									marginInlineEnd: 8,
									color: wacToken('color.primary.black'),
								}),
							]}
							htmlFor="unit-count"
						>
							{unitCountLabel}
						</label>
						<div
							css={css({
								padding: '16px 0',
								justifyContent: 'center',
								alignItems: 'center',
								width: 'fit-content',
								[media.above.sm]: {
									margin: 0,
									padding: 0,
									display: 'inline-flex',
									flex: 1,
								},
							})}
						>
							<input
								id="unit-count"
								type="number"
								css={inputCss}
								value={visualUnitCount}
								onChange={handleUnitCountChange}
								onBlur={handleUnitCountBlurChange}
								data-event-component="inputNumber"
								data-event-container="priceBlock"
								data-event="changed"
							/>
						</div>
						<label css={inputTermStyle} htmlFor="unit-term">
							{unitsLabel}
						</label>
					</div>
					{hasMonthlyBilling && hasAnnualBilling && (
						<div>
							<RadioButtons
								wrap={false}
								dataProductKey={dataProductKey}
								selectedButtonId={billingCycle}
								onChange={handleRadioChange}
								groupLabel={
									<legend css={[labelStyles, legendStyles]}>
										<FormattedMessage
											id="Pricing.priceFinderInputs.billMeText"
											defaultMessage="Bill me:"
										/>
									</legend>
								}
								buttons={[
									{
										id: 'MONTHLY',
										label: (
											<FormattedMessage
												id="Pricing.priceFinderInputs.monthly"
												defaultMessage="Monthly"
											/>
										),
									},
									{
										id: 'ANNUAL',
										label: (
											<FormattedMessage
												id="Pricing.pricingCalculatorInputs.anually"
												defaultMessage="Annually"
											/>
										),
										lozenge: {
											appearance: 'blue',
											text: (
												<FormattedMessage
													id="Pricing.pricingCalculatorInputs.saveUpTo"
													defaultMessage="SAVE UP TO 17%"
												/>
											),
										},
									},
								]}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const labelStyles = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	letterSpacing: '.3px',
	fontWeight: '600',
	whiteSpace: 'nowrap',
});

const legendStyles = css({
	float: 'left',
});

const inputTermStyle = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#091E42',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	letterSpacing: '.3px',
	fontWeight: '400',
	flex: 1,
});

const inputCss = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#172B4D',
	fontWeight: '400',
	fontSize: '1rem',
	lineHeight: '24px',
	marginInlineEnd: '5px',
	padding: '8px 6px',
	width: '75px',
	borderRadius: '4px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	background: '#F4F5F7',
	border: 'none',
	'-moz-appearance': 'textfield',
	'::-webkit-inner-spin-button, ::-webkit-outer-spin-button': {
		'-webkit-appearance': 'none',
	},
});
